
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// @ts-ignore
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Button } from '@/components/ui/button';
import { Plus, Settings, ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
// @ts-ignore
import _ from 'lodash';

// Styles for grid layout
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import apiService from '@/services/api';
import { Dashboard, Widget } from '@/types';
import WidgetCard from './WidgetCard';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import VisualQueryBuilder from "./VisualQueryBuilder";
import { DatabaseConnection } from "@/types";

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardView = () => {
    const { dashboardId } = useParams<{ dashboardId: string }>();
    const navigate = useNavigate();

    const [dashboard, setDashboard] = useState<Dashboard | null>(null);
    const [widgets, setWidgets] = useState<Widget[]>([]);
    const [layouts, setLayouts] = useState<{ lg: any[] }>({ lg: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
    const [selectedConnectionId, setSelectedConnectionId] = useState<string>("");
    const [connections, setConnections] = useState<DatabaseConnection[]>([]);

    useEffect(() => {
        if (isAddWidgetOpen) {
            loadConnections();
        }
    }, [isAddWidgetOpen]);

    const loadConnections = async () => {
        try {
            const conns = await apiService.getDatabaseConnections();
            setConnections(conns);
        } catch (error) {
            toast.error("Failed to load connections");
        }
    };

    const handleWidgetSave = async (widgetConfig: any) => {
        try {
            const newWidget = await apiService.createWidget(dashboardId!, {
                ...widgetConfig,
                layout: { x: 0, y: 0, w: 6, h: 4 }
            });
            setWidgets([...widgets, newWidget]);
            toast.success("Widget added!");
            setIsAddWidgetOpen(false);
            setSelectedConnectionId("");
        } catch (error) {
            console.error("Failed to save widget", error);
            toast.error("Failed to save widget");
        }
    };

    useEffect(() => {
        if (dashboardId) {
            loadDashboardData();
        }
    }, [dashboardId]);

    const loadDashboardData = async () => {
        setIsLoading(true);
        try {
            if (!dashboardId) return;
            const [dashData, widgetsData] = await Promise.all([
                apiService.getDashboard(dashboardId),
                apiService.getWidgets(dashboardId)
            ]);

            setDashboard(dashData);
            setWidgets(widgetsData);

            // Generate layout from widgets
            const currentLayout = widgetsData.map(w => ({
                i: w.id,
                x: w.layout?.x || 0,
                y: w.layout?.y || 0,
                w: w.layout?.w || 4,
                h: w.layout?.h || 4,
                minW: 2,
                minH: 2
            }));

            setLayouts({ lg: currentLayout });
        } catch (error) {
            console.error('Failed to load dashboard:', error);
            toast.error('Failed to load dashboard');
            navigate('/analytics');
        } finally {
            setIsLoading(false);
        }
    };

    const onLayoutChange = (_currentLayout: any[], allLayouts: any) => {
        // Find changed items
        // We only care if the layout actually changed from initial load
        // For MVP, we'll just track if we have changes
        setLayouts(allLayouts);
        setHasUnsavedChanges(true);
    };

    const saveLayout = async () => {
        if (!widgets.length) return;

        setIsSaving(true);
        try {
            // Update all widgets with new layout positions
            const updatePromises = widgets.map(widget => {
                const layoutItem = layouts.lg.find(l => l.i === widget.id);
                if (!layoutItem) return Promise.resolve();

                // Only update if changed? 
                // For simplicity, update all or check diff.
                // Assuming backend handles no-op efficiently or we don't care about minor optimization yet.

                return apiService.updateWidget(widget.id, {
                    layout: {
                        x: layoutItem.x,
                        y: layoutItem.y,
                        w: layoutItem.w,
                        h: layoutItem.h
                    }
                });
            });

            await Promise.all(updatePromises);
            toast.success('Dashboard layout saved');
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error('Failed to save layout:', error);
            toast.error('Failed to save layout');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteWidget = useCallback(async (widgetId: string) => {
        try {
            await apiService.deleteWidget(widgetId);
            setWidgets(prev => prev.filter(w => w.id !== widgetId));
            setLayouts(prev => ({
                ...prev,
                lg: prev.lg.filter(l => l.i !== widgetId)
            }));
            toast.success('Widget removed');
        } catch (error) {
            console.error('Failed to delete widget:', error);
            toast.error('Failed to remove widget');
        }
    }, []);

    if (isLoading) {
        return <div className="flex h-screen items-center justify-center">Loading dashboard...</div>;
    }

    if (!dashboard) {
        return <div className="flex h-screen items-center justify-center">Dashboard not found</div>;
    }

    return (
        <div className="container mx-auto p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/analytics')}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">{dashboard.title}</h1>
                        <p className="text-sm text-muted-foreground">{dashboard.description || 'No description'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasUnsavedChanges && (
                        <Button onClick={saveLayout} disabled={isSaving} variant="default">
                            {isSaving ? 'Saving...' : 'Save Layout'}
                            <Save className="ml-2 h-4 w-4" />
                        </Button>
                    )}
                    <Button variant="outline">
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                    </Button>
                    <Dialog open={isAddWidgetOpen} onOpenChange={setIsAddWidgetOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Widget
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[90vw] w-[1200px] h-[90vh] flex flex-col p-6 gap-0">
                            <DialogHeader className="mb-4 shrink-0">
                                <DialogTitle>Add Widget</DialogTitle>
                            </DialogHeader>
                            {!selectedConnectionId ? (
                                <div className="p-4 flex-1 overflow-auto">
                                    <h3 className="mb-4 text-lg font-medium">Select a Data Source</h3>
                                    <div className="grid grid-cols-3 gap-4">
                                        {connections.map(conn => (
                                            <Card key={conn.id} className="p-4 cursor-pointer hover:bg-accent/10 hover:border-primary transition-all" onClick={() => setSelectedConnectionId(conn.id!)}>
                                                <h4 className="font-bold">{conn.alias}</h4>
                                                <p className="text-sm text-muted-foreground">{conn.database}</p>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 min-h-0 w-full">
                                    <VisualQueryBuilder
                                        dbConnectionId={selectedConnectionId}
                                        onSave={handleWidgetSave}
                                    />
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Grid */}
            <div className="bg-muted/10 min-h-[600px] rounded-lg border-2 border-dashed border-muted/20 p-0">
                {widgets.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                        <p>No widgets yet.</p>
                        <p className="text-sm mt-2">Generate charts in chat and pin them here!</p>
                    </div>
                ) : (
                    <ResponsiveGridLayout
                        className="layout"
                        layouts={layouts}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                        rowHeight={100}
                        onLayoutChange={onLayoutChange}
                        isDraggable
                        isResizable
                        margin={[4, 4]}
                    >
                        {widgets.map(widget => (
                            <div key={widget.id} className="bg-background border rounded-lg shadow-sm overflow-hidden">
                                <WidgetCard
                                    widget={widget}
                                    dashboardId={dashboardId!}
                                    onDelete={handleDeleteWidget}
                                />
                            </div>
                        ))}
                    </ResponsiveGridLayout>
                )}
            </div>
        </div>
    );
};

export default DashboardView;
